import { PROFESSIONAL_PRICE_LABEL } from "./pricing";

export const SITE_URL = "https://aiacademypro.online";
export const SITE_NAME = "AI Academy Pro";
export const SUPPORT_EMAIL = "support@aiacademypro.online";
export const TODAY_ISO = "2026-05-28";

export type SeoFaq = {
  question: string;
  answer: string;
};

export type SeoSection = {
  heading: string;
  body: string;
};

export type SeoPage = {
  path: string;
  title: string;
  description: string;
  h1: string;
  summary: string;
  keywords: string[];
  sections: SeoSection[];
  faqs: SeoFaq[];
  relatedPaths: string[];
  changefreq: "daily" | "weekly" | "monthly" | "yearly";
  priority: string;
  pageType: "home" | "platform" | "programmatic" | "support" | "legal";
};

export const staticSeoPages: SeoPage[] = [
  {
    path: "/",
    title: "AI Courses & Machine Learning Certification | AI Academy Pro",
    description: "Learn artificial intelligence through story-based AI courses, machine learning projects, starter kits, practice labs, and professional certificates.",
    h1: "AI Courses and Machine Learning Certification",
    summary: "AI Academy Pro is a practical AI education platform for learning machine learning, deep learning, Python, generative AI, and production AI development through courses, projects, starter kits, and certificates.",
    keywords: ["AI courses", "machine learning certification", "artificial intelligence training", "AI Academy Pro"],
    sections: [
      {
        heading: "What AI Academy Pro Includes",
        body: "Learners get structured AI courses, guided learning tracks, starter kits, code practice, career resources, and professional certificate paths.",
      },
      {
        heading: "Best For",
        body: "The platform is built for beginners, software builders, analysts, managers, and career changers who want practical AI skills instead of isolated theory.",
      },
    ],
    faqs: [
      {
        question: "What can I learn at AI Academy Pro?",
        answer: "You can learn artificial intelligence foundations, machine learning, deep learning, Python for AI, generative AI development, vector databases, computer vision, NLP, and responsible enterprise AI.",
      },
      {
        question: "Does AI Academy Pro include certificates?",
        answer: "Yes. AI Academy Pro includes professional certificate paths tied to structured AI courses, practice work, and learning tracks.",
      },
    ],
    relatedPaths: ["/courses", "/starter-kits", "/playground", "/career"],
    changefreq: "daily",
    priority: "1.0",
    pageType: "home",
  },
  {
    path: "/courses",
    title: "AI Courses Online for Practical Machine Learning | AI Academy Pro",
    description: "Browse practical AI courses covering machine learning, deep learning, Python, generative AI, RAG, computer vision, NLP, and AI ethics.",
    h1: "AI Courses Online for Practical Machine Learning",
    summary: "AI Academy Pro courses help learners move from AI fundamentals into production-ready projects, certificates, and career-ready skills.",
    keywords: ["AI courses online", "machine learning courses", "deep learning course", "Python AI course"],
    sections: [
      {
        heading: "Course Topics",
        body: "The course catalog covers AI foundations, Python, machine learning, neural networks, computer vision, NLP, vector databases, RAG, generative AI, and enterprise AI.",
      },
      {
        heading: "Learning Style",
        body: "Courses are organized around explanations, examples, exercises, projects, and track milestones so learners can build usable AI systems.",
      },
    ],
    faqs: [
      {
        question: "Are the AI courses beginner friendly?",
        answer: "Yes. Beginner paths start with AI concepts and Python basics before progressing into machine learning and deep learning.",
      },
      {
        question: "Do the courses include projects?",
        answer: "Yes. Courses connect to starter kits, coding practice, and production-style projects so learners can apply each concept.",
      },
    ],
    relatedPaths: ["/learn/ai-courses-for-beginners", "/learn/machine-learning-certification", "/learn/python-for-ai"],
    changefreq: "daily",
    priority: "0.9",
    pageType: "platform",
  },
  {
    path: "/starter-kits",
    title: "AI Starter Kits for Production Projects | AI Academy Pro",
    description: "Use AI starter kits for chatbots, RAG apps, ML dashboards, computer vision tools, and deployable AI applications.",
    h1: "AI Starter Kits for Production Projects",
    summary: "Starter kits help learners move from course concepts to working AI applications with deployable templates and guided implementation patterns.",
    keywords: ["AI starter kits", "AI project templates", "RAG starter kit", "AI app templates"],
    sections: [
      {
        heading: "Project Templates",
        body: "Starter kits include templates for assistants, analytics dashboards, RAG search, computer vision workflows, automation tools, and AI-enabled web apps.",
      },
      {
        heading: "Deployment Focus",
        body: "Each kit is designed around realistic implementation steps so learners can understand both the model behavior and the application architecture.",
      },
    ],
    faqs: [
      {
        question: "What is an AI starter kit?",
        answer: "An AI starter kit is a guided project template that helps you build and deploy a working AI application faster than starting from a blank file.",
      },
      {
        question: "Can starter kits support portfolio work?",
        answer: "Yes. Starter kits are useful for portfolio projects because they connect AI concepts to visible, working applications.",
      },
    ],
    relatedPaths: ["/learn/ai-starter-kits", "/learn/vector-databases-rag", "/playground"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "platform",
  },
  {
    path: "/playground",
    title: "AI Practice Labs and Code Playground | AI Academy Pro",
    description: "Practice AI coding with guided labs, examples, and hands-on exercises for Python, machine learning, generative AI, and app development.",
    h1: "AI Practice Labs and Code Playground",
    summary: "The AI practice area helps learners test ideas, write code, and reinforce course concepts through hands-on AI development work.",
    keywords: ["AI practice labs", "AI code playground", "machine learning practice", "Python AI exercises"],
    sections: [
      {
        heading: "Hands-On Practice",
        body: "Practice labs focus on applying AI concepts in code, testing implementation choices, and learning from concrete examples.",
      },
      {
        heading: "Skill Reinforcement",
        body: "The playground supports learners who want to move beyond passive lessons and build confidence with AI development workflows.",
      },
    ],
    faqs: [
      {
        question: "Why use AI practice labs?",
        answer: "Practice labs make AI concepts easier to retain because learners can write, test, debug, and improve real examples.",
      },
      {
        question: "Does the playground support beginners?",
        answer: "Yes. It supports guided practice while still being useful for learners who want to experiment with more advanced AI ideas.",
      },
    ],
    relatedPaths: ["/courses", "/learn/ai-practice-labs", "/starter-kits"],
    changefreq: "weekly",
    priority: "0.7",
    pageType: "platform",
  },
  {
    path: "/career",
    title: "AI Career Hub for Skills, Resume, and Roadmap | AI Academy Pro",
    description: "Plan your AI career with skill paths, resume tools, project guidance, interview preparation, and role-focused learning roadmaps.",
    h1: "AI Career Hub for Skills, Resume, and Roadmap",
    summary: "The AI Career Hub connects course work to career outcomes by helping learners map skills, projects, resumes, and interview preparation.",
    keywords: ["AI career roadmap", "machine learning career", "AI resume", "AI job skills"],
    sections: [
      {
        heading: "Career Planning",
        body: "Learners can align their AI study plan to target roles such as AI engineer, ML engineer, data scientist, automation builder, and AI product specialist.",
      },
      {
        heading: "Portfolio Signals",
        body: "The platform emphasizes projects, certificates, and practical skills that can be shown in resumes and interviews.",
      },
    ],
    faqs: [
      {
        question: "Can AI Academy Pro help with AI career planning?",
        answer: "Yes. The career hub links learning paths, projects, certificates, and resume guidance to practical AI job outcomes.",
      },
      {
        question: "What AI roles can learners prepare for?",
        answer: "Learners can prepare for AI engineer, machine learning engineer, data scientist, automation developer, and AI product or operations roles.",
      },
    ],
    relatedPaths: ["/roadmap", "/learn/ai-career-roadmap", "/certifications"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "platform",
  },
  {
    path: "/certifications",
    title: "AI Certification Paths for Professional Skills | AI Academy Pro",
    description: "Earn AI certificates by completing structured learning tracks in machine learning, deep learning, Python, generative AI, and applied AI.",
    h1: "AI Certification Paths for Professional Skills",
    summary: "AI Academy Pro certification paths help learners show structured progress through practical AI topics and project-based learning.",
    keywords: ["AI certification", "machine learning certificate", "deep learning certificate", "AI professional certificate"],
    sections: [
      {
        heading: "Credential Focus",
        body: "Certificates are connected to learning tracks, course progress, and practical skills rather than one-off quizzes.",
      },
      {
        heading: "Professional Use",
        body: "Certification paths are designed to help learners communicate their AI capabilities to employers, clients, and teams.",
      },
    ],
    faqs: [
      {
        question: "What is an AI professional certificate?",
        answer: "An AI professional certificate is a credential that signals completion of structured AI learning across courses, projects, and practical assessments.",
      },
      {
        question: "Is a machine learning certificate useful?",
        answer: "A machine learning certificate is useful when it is backed by practical projects, clear topics, and evidence of applied skills.",
      },
    ],
    relatedPaths: ["/learn/machine-learning-certification", "/courses", "/roadmap"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "platform",
  },
  {
    path: "/roadmap",
    title: "AI Learning Roadmap from Beginner to Expert | AI Academy Pro",
    description: "Follow an AI learning roadmap from foundations to Python, machine learning, deep learning, generative AI, projects, and certificates.",
    h1: "AI Learning Roadmap from Beginner to Expert",
    summary: "The roadmap gives learners a clear order for studying artificial intelligence, practicing with projects, and preparing for professional AI work.",
    keywords: ["AI learning roadmap", "learn AI roadmap", "machine learning roadmap", "AI beginner roadmap"],
    sections: [
      {
        heading: "Recommended Order",
        body: "Start with AI foundations and Python, then study machine learning, deep learning, generative AI, RAG, specialization topics, and portfolio projects.",
      },
      {
        heading: "Milestones",
        body: "Each stage is tied to practical milestones so learners understand what to study, what to build, and how to prove progress.",
      },
    ],
    faqs: [
      {
        question: "What is the best AI learning roadmap?",
        answer: "A strong AI learning roadmap starts with foundations and Python, then moves into machine learning, deep learning, generative AI, applied projects, and certificates.",
      },
      {
        question: "How long does it take to learn AI?",
        answer: "The timeline depends on your background, but a structured roadmap helps you build useful AI skills faster by avoiding random course hopping.",
      },
    ],
    relatedPaths: ["/learn/ai-courses-for-beginners", "/learn/ai-career-roadmap", "/courses"],
    changefreq: "monthly",
    priority: "0.7",
    pageType: "platform",
  },
  {
    path: "/documentation",
    title: "AI Learning Resources and Documentation | AI Academy Pro",
    description: "Read AI learning resources, platform documentation, guides, and references for courses, starter kits, practice labs, and certificates.",
    h1: "AI Learning Resources and Documentation",
    summary: "The resources area helps learners understand platform workflows, AI study concepts, starter kit usage, and practical learning guidance.",
    keywords: ["AI learning resources", "AI documentation", "machine learning guides", "AI tutorials"],
    sections: [
      {
        heading: "Learning References",
        body: "Documentation supports learners who need quick references, explanations, and guidance while working through AI courses and projects.",
      },
      {
        heading: "Platform Guidance",
        body: "Resources explain how courses, starter kits, practice labs, and career tools fit together.",
      },
    ],
    faqs: [
      {
        question: "What resources are included?",
        answer: "AI Academy Pro includes documentation, tutorials, guides, starter kit references, career resources, and support content.",
      },
      {
        question: "Are resources useful for AI Overviews and answer engines?",
        answer: "Clear, structured resources help both humans and answer engines understand what the platform teaches and how topics connect.",
      },
    ],
    relatedPaths: ["/blog", "/research", "/learn/generative-ai-development"],
    changefreq: "monthly",
    priority: "0.6",
    pageType: "support",
  },
  {
    path: "/help",
    title: "AI Academy Pro Help Center and Support",
    description: "Get help with AI Academy Pro accounts, courses, certificates, starter kits, payments, technical support, and learning workflows.",
    h1: "AI Academy Pro Help Center and Support",
    summary: "The Help Center gives learners support for accounts, access, learning tracks, certificates, billing, starter kits, and technical issues.",
    keywords: ["AI Academy Pro help", "AI course support", "AI certificate help", "AI Academy support"],
    sections: [
      {
        heading: "Support Topics",
        body: "Support covers account access, course navigation, certificates, starter kits, payments, technical issues, and learning guidance.",
      },
      {
        heading: "Contact",
        body: `Learners can contact support at ${SUPPORT_EMAIL} for account or platform questions.`,
      },
    ],
    faqs: [
      {
        question: "How do I contact AI Academy Pro support?",
        answer: `You can contact AI Academy Pro support at ${SUPPORT_EMAIL}.`,
      },
      {
        question: "Can support help with certificates?",
        answer: "Yes. Support can help with certificate access, verification, and account questions.",
      },
    ],
    relatedPaths: ["/contact", "/courses", "/payment-plans"],
    changefreq: "monthly",
    priority: "0.5",
    pageType: "support",
  },
  {
    path: "/payment-plans",
    title: `${PROFESSIONAL_PRICE_LABEL} AI Academy Pro Access and Course Pricing`,
    description: `Get one-year AI Academy Pro access for ${PROFESSIONAL_PRICE_LABEL} with AI courses, starter kits, practice labs, career tools, and professional certificates.`,
    h1: `${PROFESSIONAL_PRICE_LABEL} AI Academy Pro Access and Course Pricing`,
    summary: `AI Academy Pro offers one clear paid option: ${PROFESSIONAL_PRICE_LABEL} for one-year professional access to courses, starter kits, practice labs, career tools, and certificates.`,
    keywords: ["AI course pricing", `${PROFESSIONAL_PRICE_LABEL} AI course`, "AI Academy Pro price", "AI certification cost"],
    sections: [
      {
        heading: "Simple Pricing",
        body: `The current AI Academy Pro offer is a ${PROFESSIONAL_PRICE_LABEL} one-year professional access plan with no confusing student or professional split.`,
      },
      {
        heading: "Included Value",
        body: "Access includes AI learning tracks, course material, starter kits, practice tools, certificates, and platform updates during the active term.",
      },
    ],
    faqs: [
      {
        question: "How much does AI Academy Pro cost?",
        answer: `AI Academy Pro currently offers one ${PROFESSIONAL_PRICE_LABEL} one-year professional access option.`,
      },
      {
        question: `What is included in the ${PROFESSIONAL_PRICE_LABEL} access?`,
        answer: "The plan includes AI courses, starter kits, practice labs, career tools, certificate paths, and platform updates during the active term.",
      },
    ],
    relatedPaths: ["/courses", "/certifications", "/register"],
    changefreq: "monthly",
    priority: "0.7",
    pageType: "platform",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | AI Academy Pro",
    description: "Read the AI Academy Pro privacy policy for details about account data, platform usage, payments, email, and learner privacy.",
    h1: "Privacy Policy",
    summary: "The privacy policy explains how AI Academy Pro handles account, payment, email, and learning platform data.",
    keywords: ["AI Academy Pro privacy", "privacy policy"],
    sections: [
      {
        heading: "Privacy Overview",
        body: "AI Academy Pro provides privacy information for learners, account holders, and platform users.",
      },
    ],
    faqs: [],
    relatedPaths: ["/terms", "/help"],
    changefreq: "yearly",
    priority: "0.3",
    pageType: "legal",
  },
  {
    path: "/terms",
    title: "Terms of Service | AI Academy Pro",
    description: "Read the AI Academy Pro terms of service for platform access, course usage, payments, certificates, and account responsibilities.",
    h1: "Terms of Service",
    summary: "The terms of service explain usage rules for AI Academy Pro courses, payments, certificates, accounts, and platform access.",
    keywords: ["AI Academy Pro terms", "terms of service"],
    sections: [
      {
        heading: "Terms Overview",
        body: "AI Academy Pro provides terms for learners who use the course platform, starter kits, certificates, and account features.",
      },
    ],
    faqs: [],
    relatedPaths: ["/privacy", "/help"],
    changefreq: "yearly",
    priority: "0.3",
    pageType: "legal",
  },
];

export const programmaticSeoPages: SeoPage[] = [
  {
    path: "/learn/ai-courses-for-beginners",
    title: "AI Courses for Beginners: Start Learning AI | AI Academy Pro",
    description: "Start learning AI with beginner-friendly courses in artificial intelligence, Python, machine learning basics, projects, and certificates.",
    h1: "AI Courses for Beginners",
    summary: "Bottom line: beginners should learn AI by combining plain-language concepts, Python practice, machine learning fundamentals, and small portfolio projects.",
    keywords: ["AI courses for beginners", "learn AI for beginners", "beginner artificial intelligence course"],
    sections: [
      {
        heading: "Best First Steps",
        body: "Start with AI vocabulary, data basics, Python, model concepts, prompt workflows, and simple prediction or automation projects.",
      },
      {
        heading: "What To Build",
        body: "Beginner projects can include a chatbot, classification model, summarizer, simple RAG search tool, or AI workflow assistant.",
      },
      {
        heading: "How AI Academy Pro Helps",
        body: "AI Academy Pro organizes beginner courses, practice labs, starter kits, and certificates into a guided path so learners know what to do next.",
      },
    ],
    faqs: [
      {
        question: "What is the best AI course for beginners?",
        answer: "The best AI course for beginners teaches concepts first, then Python, machine learning basics, practice exercises, and small applied projects.",
      },
      {
        question: "Do I need math before learning AI?",
        answer: "You can start with basic AI concepts and Python before going deeper into linear algebra, statistics, probability, and optimization.",
      },
    ],
    relatedPaths: ["/courses", "/learn/python-for-ai", "/learn/machine-learning-certification"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/machine-learning-certification",
    title: "Machine Learning Certification Path | AI Academy Pro",
    description: "Follow a machine learning certification path with supervised learning, model evaluation, Python projects, deep learning, and certificates.",
    h1: "Machine Learning Certification Path",
    summary: "Bottom line: a useful machine learning certification should prove that you understand models, data, evaluation, deployment basics, and applied projects.",
    keywords: ["machine learning certification", "ML certification", "machine learning certificate online"],
    sections: [
      {
        heading: "Core Skills",
        body: "A strong path covers regression, classification, clustering, feature engineering, validation, metrics, bias, overfitting, and model monitoring.",
      },
      {
        heading: "Project Evidence",
        body: "Learners should complete projects that show data preparation, model selection, evaluation, explanation, and practical tradeoffs.",
      },
      {
        heading: "Certificate Value",
        body: "Certificates are most valuable when they connect to clear skills and portfolio work rather than only course attendance.",
      },
    ],
    faqs: [
      {
        question: "Is a machine learning certification worth it?",
        answer: "It is worth it when the certification is paired with practical projects, portfolio evidence, and a clear understanding of ML workflows.",
      },
      {
        question: "What should a machine learning certificate include?",
        answer: "It should include supervised learning, unsupervised learning, model evaluation, Python practice, applied projects, and responsible AI concepts.",
      },
    ],
    relatedPaths: ["/certifications", "/learn/deep-learning-neural-networks", "/learn/python-for-ai"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/deep-learning-neural-networks",
    title: "Deep Learning and Neural Networks Course | AI Academy Pro",
    description: "Learn deep learning and neural networks with practical lessons on tensors, training loops, CNNs, transformers, embeddings, and projects.",
    h1: "Deep Learning and Neural Networks Course",
    summary: "Bottom line: deep learning is easiest to learn when you connect neural network theory to tensors, training loops, architectures, and applied projects.",
    keywords: ["deep learning course", "neural networks course", "learn deep learning"],
    sections: [
      {
        heading: "Topics Covered",
        body: "Deep learning study should cover tensors, loss functions, backpropagation, optimizers, CNNs, RNNs, transformers, embeddings, and evaluation.",
      },
      {
        heading: "Practical Projects",
        body: "Useful projects include image classification, text classification, embedding search, sequence modeling, and small transformer workflows.",
      },
      {
        heading: "Learning Path",
        body: "AI Academy Pro places deep learning after foundations, Python, and machine learning so learners have the context to understand model behavior.",
      },
    ],
    faqs: [
      {
        question: "Should I learn machine learning before deep learning?",
        answer: "Yes. Learning machine learning first helps you understand data, evaluation, features, and model tradeoffs before neural networks.",
      },
      {
        question: "What projects help with deep learning?",
        answer: "Good projects include computer vision classifiers, NLP classifiers, embedding search, RAG pipelines, and transformer-based assistants.",
      },
    ],
    relatedPaths: ["/learn/machine-learning-certification", "/learn/computer-vision-course", "/learn/natural-language-processing-course"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/python-for-ai",
    title: "Python for AI: Learn Programming for AI | AI Academy Pro",
    description: "Learn Python for AI with data structures, NumPy, pandas, model workflows, APIs, automation, and machine learning projects.",
    h1: "Python for AI",
    summary: "Bottom line: Python for AI should focus on data handling, functions, APIs, notebooks, packages, model workflows, and project structure.",
    keywords: ["Python for AI", "learn Python AI", "Python machine learning course"],
    sections: [
      {
        heading: "Python Skills",
        body: "Learners need functions, data structures, files, APIs, virtual environments, notebooks, NumPy, pandas, visualization, and testing basics.",
      },
      {
        heading: "AI Workflows",
        body: "Python AI work includes loading data, preparing features, calling models, evaluating outputs, building APIs, and deploying small tools.",
      },
      {
        heading: "Practice Path",
        body: "AI Academy Pro pairs Python lessons with AI exercises and starter kits so programming practice connects to real AI outcomes.",
      },
    ],
    faqs: [
      {
        question: "Is Python required for AI?",
        answer: "Python is the most common language for AI and machine learning because it has strong libraries, examples, and production tooling.",
      },
      {
        question: "How much Python do I need before machine learning?",
        answer: "You need enough Python to work with data, functions, packages, notebooks, APIs, and basic debugging before deeper machine learning projects.",
      },
    ],
    relatedPaths: ["/learn/ai-courses-for-beginners", "/learn/ai-practice-labs", "/courses"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/ai-starter-kits",
    title: "AI Starter Kits for Chatbots, RAG, and ML Apps",
    description: "Build AI apps faster with starter kits for chatbots, RAG search, machine learning dashboards, computer vision, and automation.",
    h1: "AI Starter Kits for Chatbots, RAG, and ML Apps",
    summary: "Bottom line: AI starter kits help learners turn concepts into working applications by giving them a practical architecture and build path.",
    keywords: ["AI starter kits", "chatbot starter kit", "RAG starter kit", "ML app template"],
    sections: [
      {
        heading: "Starter Kit Types",
        body: "Useful starter kits include chatbots, retrieval augmented generation, analytics dashboards, classification apps, vision tools, and automation workflows.",
      },
      {
        heading: "Why They Work",
        body: "Starter kits reduce blank-page friction and let learners focus on architecture, model behavior, data flow, and user experience.",
      },
      {
        heading: "Production Readiness",
        body: "AI Academy Pro starter kits emphasize practical project structure, deployment thinking, security basics, and maintainable application patterns.",
      },
    ],
    faqs: [
      {
        question: "What can I build with AI starter kits?",
        answer: "You can build AI chatbots, RAG search tools, ML dashboards, computer vision demos, automation tools, and portfolio-ready applications.",
      },
      {
        question: "Are starter kits good for learning?",
        answer: "Yes. Starter kits are useful because they connect course concepts to visible working projects.",
      },
    ],
    relatedPaths: ["/starter-kits", "/learn/vector-databases-rag", "/learn/generative-ai-development"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/ai-practice-labs",
    title: "AI Practice Labs for Hands-On Learning | AI Academy Pro",
    description: "Use AI practice labs to reinforce Python, machine learning, prompt workflows, RAG, generative AI, and model evaluation skills.",
    h1: "AI Practice Labs for Hands-On Learning",
    summary: "Bottom line: AI practice labs help learners retain skills by turning abstract concepts into repeated coding, testing, and debugging work.",
    keywords: ["AI practice labs", "hands-on AI learning", "machine learning exercises", "AI coding practice"],
    sections: [
      {
        heading: "Practice Topics",
        body: "Practice can cover Python basics, prompt design, model evaluation, data preparation, vector search, classification, and deployment tasks.",
      },
      {
        heading: "Learning Loop",
        body: "A strong practice loop includes an explanation, a small task, immediate feedback, reflection, and a harder follow-up challenge.",
      },
      {
        heading: "Outcome",
        body: "Practice labs help learners build fluency so they can apply AI techniques without needing to rewatch lessons repeatedly.",
      },
    ],
    faqs: [
      {
        question: "How do AI practice labs help?",
        answer: "They help by making learners apply concepts in code, inspect errors, improve outputs, and build confidence through repetition.",
      },
      {
        question: "What should I practice first?",
        answer: "Start with Python, data handling, prompts, simple models, and evaluation before moving into RAG and deep learning projects.",
      },
    ],
    relatedPaths: ["/playground", "/learn/python-for-ai", "/learn/ai-courses-for-beginners"],
    changefreq: "weekly",
    priority: "0.7",
    pageType: "programmatic",
  },
  {
    path: "/learn/generative-ai-development",
    title: "Generative AI Development Course | AI Academy Pro",
    description: "Learn generative AI development with prompts, LLM APIs, embeddings, retrieval, evaluation, safety, and production app patterns.",
    h1: "Generative AI Development Course",
    summary: "Bottom line: generative AI development combines prompt design, LLM APIs, retrieval, evaluation, safety, and product engineering.",
    keywords: ["generative AI development", "LLM course", "generative AI course", "AI app development"],
    sections: [
      {
        heading: "Developer Skills",
        body: "Generative AI developers need prompt workflows, API usage, streaming, embeddings, retrieval, evaluation, guardrails, logging, and UX patterns.",
      },
      {
        heading: "Production Patterns",
        body: "Production applications need cost controls, safety checks, observability, fallback behavior, secure secrets, and clear user feedback.",
      },
      {
        heading: "What To Build",
        body: "Useful projects include assistants, content tools, document search, RAG workflows, agentic automation, and domain-specific copilots.",
      },
    ],
    faqs: [
      {
        question: "What is generative AI development?",
        answer: "Generative AI development is the process of building applications that use LLMs or other generative models to create, reason, retrieve, and assist.",
      },
      {
        question: "Do generative AI apps need evaluation?",
        answer: "Yes. Evaluation is essential because LLM outputs can vary, and production systems need quality checks, safety controls, and monitoring.",
      },
    ],
    relatedPaths: ["/learn/vector-databases-rag", "/learn/ai-starter-kits", "/playground"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/vector-databases-rag",
    title: "Vector Databases and RAG Course | AI Academy Pro",
    description: "Learn vector databases and RAG with embeddings, chunking, retrieval, ranking, evaluation, and production search patterns.",
    h1: "Vector Databases and RAG Course",
    summary: "Bottom line: RAG systems work best when content is chunked well, embedded consistently, retrieved accurately, and evaluated against real questions.",
    keywords: ["vector databases course", "RAG course", "retrieval augmented generation", "embeddings course"],
    sections: [
      {
        heading: "Core Concepts",
        body: "RAG learning should cover chunking, embeddings, vector indexes, metadata filters, hybrid search, reranking, citations, and answer evaluation.",
      },
      {
        heading: "Application Patterns",
        body: "Common RAG applications include documentation search, customer support, policy assistants, research tools, and internal knowledge copilots.",
      },
      {
        heading: "Quality Controls",
        body: "Good RAG systems need source grounding, answer constraints, retrieval tests, freshness handling, and clear failure states.",
      },
    ],
    faqs: [
      {
        question: "What is RAG in AI?",
        answer: "RAG means retrieval augmented generation. It retrieves relevant information and gives it to a generative model so answers are more grounded.",
      },
      {
        question: "Why use vector databases for AI?",
        answer: "Vector databases help AI applications find semantically similar content through embeddings, which is useful for search, recommendations, and RAG.",
      },
    ],
    relatedPaths: ["/learn/generative-ai-development", "/learn/ai-starter-kits", "/documentation"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
  {
    path: "/learn/computer-vision-course",
    title: "Computer Vision Course for AI Builders | AI Academy Pro",
    description: "Learn computer vision with image data, CNNs, classification, detection, segmentation, evaluation, and applied AI projects.",
    h1: "Computer Vision Course for AI Builders",
    summary: "Bottom line: computer vision learning should connect image data, neural networks, evaluation metrics, and practical visual AI projects.",
    keywords: ["computer vision course", "learn computer vision", "AI vision course", "image classification course"],
    sections: [
      {
        heading: "Vision Topics",
        body: "Core topics include image preprocessing, augmentation, CNNs, transfer learning, classification, object detection, segmentation, and evaluation.",
      },
      {
        heading: "Projects",
        body: "Good projects include image classifiers, defect detection, document analysis, visual search, and basic multimodal workflows.",
      },
      {
        heading: "Career Use",
        body: "Computer vision skills are useful in manufacturing, healthcare, retail, robotics, security, agriculture, and media workflows.",
      },
    ],
    faqs: [
      {
        question: "Is computer vision part of AI?",
        answer: "Yes. Computer vision is a major AI field focused on helping systems understand images, video, and visual patterns.",
      },
      {
        question: "What should I learn before computer vision?",
        answer: "Learn Python, data handling, machine learning basics, and neural network fundamentals before deeper computer vision work.",
      },
    ],
    relatedPaths: ["/learn/deep-learning-neural-networks", "/learn/python-for-ai", "/courses"],
    changefreq: "weekly",
    priority: "0.7",
    pageType: "programmatic",
  },
  {
    path: "/learn/natural-language-processing-course",
    title: "Natural Language Processing Course | AI Academy Pro",
    description: "Learn NLP with text processing, embeddings, transformers, classification, summarization, RAG, evaluation, and LLM application patterns.",
    h1: "Natural Language Processing Course",
    summary: "Bottom line: NLP skills now combine classic text processing, embeddings, transformers, retrieval, and LLM application design.",
    keywords: ["natural language processing course", "NLP course", "learn NLP", "text AI course"],
    sections: [
      {
        heading: "NLP Topics",
        body: "Important topics include tokenization, text cleaning, embeddings, classification, sequence models, transformers, summarization, search, and evaluation.",
      },
      {
        heading: "Modern NLP",
        body: "Modern NLP includes LLM prompting, retrieval, reranking, instruction following, structured extraction, and safety-aware response design.",
      },
      {
        heading: "Projects",
        body: "Learners can build classifiers, summarizers, chat assistants, document search tools, extraction workflows, and support copilots.",
      },
    ],
    faqs: [
      {
        question: "What is NLP in AI?",
        answer: "Natural language processing is the AI field focused on helping software understand, search, transform, and generate human language.",
      },
      {
        question: "Is NLP still useful with LLMs?",
        answer: "Yes. LLMs make NLP more powerful, but developers still need text processing, retrieval, evaluation, and application design skills.",
      },
    ],
    relatedPaths: ["/learn/generative-ai-development", "/learn/vector-databases-rag", "/learn/deep-learning-neural-networks"],
    changefreq: "weekly",
    priority: "0.7",
    pageType: "programmatic",
  },
  {
    path: "/learn/enterprise-ai-ethics",
    title: "Enterprise AI and AI Ethics Course | AI Academy Pro",
    description: "Learn enterprise AI and ethics with governance, risk, safety, privacy, evaluation, compliance, adoption, and responsible AI patterns.",
    h1: "Enterprise AI and AI Ethics Course",
    summary: "Bottom line: enterprise AI requires practical value, responsible governance, privacy, security, evaluation, and human oversight.",
    keywords: ["enterprise AI course", "AI ethics course", "responsible AI training", "AI governance course"],
    sections: [
      {
        heading: "Enterprise Topics",
        body: "Enterprise AI learning should cover use-case selection, ROI, workflow design, data governance, risk, privacy, security, and vendor evaluation.",
      },
      {
        heading: "Responsible AI",
        body: "Responsible AI includes fairness, transparency, explainability, consent, accountability, human review, monitoring, and safe deployment practices.",
      },
      {
        heading: "Team Adoption",
        body: "Successful AI adoption requires training, policies, measurement, stakeholder alignment, and a clear plan for production support.",
      },
    ],
    faqs: [
      {
        question: "Why is AI ethics important?",
        answer: "AI ethics matters because AI systems can affect decisions, privacy, fairness, safety, and trust in real workflows.",
      },
      {
        question: "What should enterprises learn about AI?",
        answer: "Enterprises should learn AI use cases, risk management, governance, data privacy, security, evaluation, and responsible deployment.",
      },
    ],
    relatedPaths: ["/learn/generative-ai-development", "/career", "/documentation"],
    changefreq: "monthly",
    priority: "0.7",
    pageType: "programmatic",
  },
  {
    path: "/learn/ai-career-roadmap",
    title: "AI Career Roadmap for Beginners and Builders",
    description: "Use an AI career roadmap for learning foundations, Python, machine learning, projects, certificates, resumes, and interview preparation.",
    h1: "AI Career Roadmap for Beginners and Builders",
    summary: "Bottom line: an AI career roadmap should combine learning order, projects, certificates, resume signals, and role-specific practice.",
    keywords: ["AI career roadmap", "AI career path", "machine learning career roadmap", "AI jobs skills"],
    sections: [
      {
        heading: "Roadmap Steps",
        body: "Start with AI foundations, Python, data handling, machine learning, deep learning, generative AI, projects, and a role-specific portfolio.",
      },
      {
        heading: "Career Signals",
        body: "Useful signals include certificates, deployed projects, clear GitHub examples, case studies, resumes, and interview stories.",
      },
      {
        heading: "Role Paths",
        body: "Learners can aim for AI engineer, ML engineer, data scientist, automation developer, AI analyst, or AI product specialist roles.",
      },
    ],
    faqs: [
      {
        question: "How do I start an AI career?",
        answer: "Start with foundations and Python, then build machine learning projects, learn generative AI workflows, earn practical certificates, and prepare a role-focused portfolio.",
      },
      {
        question: "Do I need a degree for an AI career?",
        answer: "Some roles prefer degrees, but practical skills, projects, certificates, and strong explanations can help learners compete for many AI-adjacent roles.",
      },
    ],
    relatedPaths: ["/career", "/roadmap", "/learn/machine-learning-certification"],
    changefreq: "weekly",
    priority: "0.8",
    pageType: "programmatic",
  },
];

export const seoPages = [...staticSeoPages, ...programmaticSeoPages];

export function normalizePath(pathname: string) {
  const pathOnly = pathname.split("?")[0].split("#")[0] || "/";
  if (pathOnly !== "/" && pathOnly.endsWith("/")) {
    return pathOnly.slice(0, -1);
  }
  return pathOnly;
}

export function getSeoPage(pathname: string) {
  const normalized = normalizePath(pathname);
  return seoPages.find((page) => page.path === normalized);
}

export function getProgrammaticSeoPage(slug: string | undefined) {
  if (!slug) return undefined;
  return programmaticSeoPages.find((page) => page.path === `/learn/${slug}`);
}

export function getRelatedSeoPages(page: SeoPage) {
  return page.relatedPaths
    .map((path) => getSeoPage(path))
    .filter((related): related is SeoPage => Boolean(related));
}
